import { useEffect, useLayoutEffect, useReducer, useRef, useState, type Ref } from 'react'
import { api, type MyWorkItem } from '../../lib/api'
import { onMyWorkChanged } from '../../lib/myWork'
import { openRequestDrawer } from '../../lib/requestDrawer'
import { chipWord } from '../../lib/status'
import { ChaseButton } from '../ChaseButton'
import { Chip } from '../StatusChip'
import { detailOnLine, inBarOrder, leadStep, onLine, restNote, turnWords, yourTurnCount, type Step } from './nextSteps'
import type { Place } from './sections'

// The vendor's next steps, drawn twice from one list (nextSteps.ts says why
// this is the only place on the page that offers a task, and why it reads the
// vendor's own My work rows).
//
// # ONE LINE, AND THE SENTENCES BEHIND IT
//
// The steps were a panel of their own above every section: a heading, a
// sentence for each step and its button, 180 px when three things waited. On a
// 768 px screen that panel, the lifecycle track and the facts strip left the
// section starting at 593 px. The steps are now one line (NextStepLine): whose
// turn it is, how many wait on the reader, and for each step what it is, which
// one, and its button. The sentences (NextStepBar) open under it with the
// lifecycle track, from the Details button on the same line (NowLine.tsx).
//
// # WHICH ONE, ON THE LINE
//
// "Risk acceptance, Decide" twice over is two buttons nobody can tell apart, so
// each step carries its detail on the line: the questionnaire's reference, or
// My work's own words for the row, and each button's name says what it is
// about (review of 11 Oct 2026). The reason of a decision is drawn whole and
// wraps, and so is any detail a cut could turn round; a name may lose its
// tail, by the rule in nextSteps.ts (detailOnLine), and what was cut is itself
// a button that opens Details at that step.
//
// # TWO ROWS, AND NEVER A SILENT CUT
//
// The line holds as many steps as two rows take. Where they do not fit, the
// details that may be cut are first cut shorter, which often makes room.
// Past that the line says "and 4 more in Details", which opens them and puts
// the reader on the first one it had left off; the count beside "Waiting for you" is
// still of all of them. Which steps stay and what the note says of the rest
// are decided in nextSteps.ts (onLine, restNote): the late, the blocked and
// the reader's own stay, and of those left off the note says how many are
// overdue or blocked, or failing that how many are the reader's. Whether they
// fit is measured, because it depends on the words and the width
// (FittedSteps).

// Late, as a chip draws a questionnaire past its date: amber, with the clock.
const OVERDUE = chipWord('questionnaire', 'ISSUED', true)

// What a step's button is about, for somebody who hears the buttons without
// the words beside them: what it is, which one, and that it is late or blocked.
function about(s: Step): string {
  return [s.what, s.detail, s.late, s.alarm?.word.toLowerCase()].filter(Boolean).join(', ')
}

function Act({ step, primary, onGo, onChased }: { step: Step; primary: boolean; onGo: (p: Place) => void; onChased: () => void }) {
  const a = step.act
  switch (a.kind) {
    case 'link':
      return (
        <a className={'button' + (primary ? ' primary' : '')} href={a.href} aria-label={a.label + ': ' + about(step)}>
          {a.label}
        </a>
      )
    case 'go':
      return (
        <button type="button" className={primary ? 'primary' : undefined} onClick={() => onGo(a.place)} aria-label={a.label + ': ' + about(step)}>
          {a.label}
        </button>
      )
    case 'chase':
      return <ChaseButton assessmentId={a.assessmentId} primary={primary} actionId="tprm.vendor,vendor-record.a30" onChased={onChased} />
    case 'request':
      // The one Approve request drawer, whichever door opened it (lib/requestDrawer.ts).
      return (
        <button
          type="button"
          className={primary ? 'primary' : undefined}
          onClick={() => openRequestDrawer(a.vendorId)}
          aria-label={a.label + ': ' + about(step)}
        >
          {a.label}
        </button>
      )
    default:
      return null
  }
}

// The vendor's own rows in My work, read again whenever a task is done.
export function useVendorWork(vendorId: string): MyWorkItem[] | null {
  const [items, setItems] = useState<MyWorkItem[] | null>(null)
  useEffect(() => {
    let live = true
    const read = () => {
      api
        .myWork({ vendor: vendorId, limit: 200 })
        .then((w) => {
          if (live) setItems(w.items ?? [])
        })
        // Without the rows the bar still shows its own steps.
        .catch(() => undefined)
    }
    read()
    const off = onMyWorkChanged(read)
    return () => {
      live = false
      off()
    }
  }, [vendorId])
  return items
}

type BarProps = {
  steps: Step[]
  onGo: (p: Place) => void
  onChanged: () => void
}

// Whose turn, with the count where it is the reader's. The count is said in
// words to a screen reader, which would otherwise hear "Waiting for you 3";
// "steps", not "waiting for you" again, so the phrase is heard once.
function Turn({ of, count }: { of: Pick<Step, 'turn' | 'who'>; count?: number }) {
  return (
    <span className={'vd-turn vd-turn-' + of.turn}>
      {turnWords(of)}
      {count != null && (
        <>
          {' '}
          <span className="vd-count" aria-hidden="true">
            {count}
          </span>
          <span className="vh">, {count + (count === 1 ? ' step' : ' steps')}</span>
        </>
      )}
    </span>
  )
}

type LineProps = BarProps & {
  // False while the sentences are open under the line: each step and its
  // button are drawn there, and a task is offered once on the page, not once
  // per place it is described.
  acts?: boolean
  // How many steps to draw before saying the rest are in Details. All of them
  // when not given.
  limit?: number | null
  // The details that may be cut, cut shorter: tried before any step is left off.
  tight?: boolean
  // Details is asked for at a step: by the note, for the first step it stood
  // for, or by a detail that was cut, for its own. With what was pressed.
  onMore?: (step: string, from: Element) => void
  listRef?: Ref<HTMLUListElement>
}

export function NextStepLine({ steps, onGo, onChanged, acts = true, limit, tight, onMore, listRef }: LineProps) {
  const { shown, hidden } = onLine(steps, limit)
  const first = shown[0]
  if (!first) return <p className="vd-quiet vd-line-turns">Nothing is waiting on this vendor.</p>
  const mine = yourTurnCount(steps)
  if (!acts) {
    return <p className="vd-line-turns">{mine > 0 ? <Turn of={{ turn: 'us' }} count={mine} /> : <Turn of={first} />}</p>
  }
  const lead = leadStep(steps)
  // The count is said once, beside the first "Waiting for you".
  const counted = shown.find((s) => s.turn === 'us')
  const leftOff = hidden[0]
  return (
    <ul ref={listRef} className="vd-line-turns" aria-label="Next steps">
      {shown.map((s, i) => {
        const before = shown[i - 1]
        const detail = detailOnLine(s, tight)
        return (
          // A run of text, so the spaces between its parts are real ones: a
          // screen reader and a copy of the line get words, not "Q4Review".
          <li key={s.key}>
            {(!before || turnWords(s) !== turnWords(before)) && (
              <>
                <Turn of={s} count={s === counted ? mine : undefined} />{' '}
              </>
            )}
            <span className="vd-line-what">
              {s.what}
              {detail && ':'}
            </span>{' '}
            {detail &&
              (detail.cut ? (
                // The rest of it is a press away, for a keyboard and a finger
                // as for a pointer: a title alone is a hover.
                <button
                  type="button"
                  className="vd-line-detail"
                  title={s.detail}
                  aria-label={s.detail + ' (cut short here, in full under Details)'}
                  onClick={(e) => onMore?.(s.key, e.currentTarget)}
                >
                  {detail.text}
                </button>
              ) : (
                <span className="vd-line-detail">{detail.text}</span>
              ))}{' '}
            {s.late && (
              <>
                <Chip s={OVERDUE}>{s.late}</Chip>{' '}
              </>
            )}
            {s.alarm && (
              <>
                <Chip s={s.alarm} />{' '}
              </>
            )}
            <Act step={s} primary={s === lead && !s.quiet} onGo={onGo} onChased={onChanged} />
          </li>
        )
      })}
      {leftOff && (
        <li className="vd-line-rest">
          <button type="button" className="linkish" onClick={(e) => onMore?.(leftOff.key, e.currentTarget)}>
            {restNote(hidden)}
          </button>
        </li>
      )}
    </ul>
  )
}

// ------------------------------------------------------------ how many fit

const LINE_ROWS = 2

type Box = { top: number; bottom: number }

// How many rows a wrapped list takes, from each item's top and bottom in
// order. An item starts a row when it begins at or below the bottom of the
// row before; items in one row may differ in height (a step with a button is
// taller than one without), so their tops are not compared.
export function rowCount(boxes: Box[]): number {
  let rows = 0
  let floor = -Infinity
  for (const b of boxes) {
    if (b.top >= floor - 1) {
      rows += 1
      floor = b.bottom
    } else {
      floor = Math.max(floor, b.bottom)
    }
  }
  return rows
}

// Whether the steps drawn fit the line: two rows of steps. The note that says
// the rest are in Details is not counted. It sits after the last step where
// there is room and under it where there is not, because counting it took a
// step off the line to make room for the sentence that said one was missing.
export function fitsLine(items: Box[]): boolean {
  return rowCount(items) <= LINE_ROWS
}

// The boxes of the steps drawn in a list, the note left out. The note is not
// a step: counted as one it took a row, and a step was left off to make room
// for the sentence that said a step was missing.
type Boxed = { classList: { contains: (name: string) => boolean }; getBoundingClientRect: () => Box }
export function stepBoxes(list: { children: ArrayLike<Boxed> }): Box[] {
  return Array.from(list.children)
    .filter((li) => !li.classList.contains('vd-line-rest'))
    .map((li) => {
      const r = li.getBoundingClientRect()
      return { top: r.top, bottom: r.bottom }
    })
}

// What to try next when the steps drawn do not fit: first the details that
// may be cut are cut shorter with no step left off, then one step fewer each
// time. Null when there is nothing left to give up.
export type Fit = { tight: boolean; limit: number | null }
export function tighter(now: Fit, total: number): Fit | null {
  if (!now.tight) return { tight: true, limit: null }
  const drawn = now.limit ?? total
  return drawn > 1 ? { tight: true, limit: drawn - 1 } : null
}

// The fit only ever gets tighter, so whatever can change how wide the words
// are has to start it again from all the steps: the steps themselves, the
// line's width, and the fonts arriving, since the words have another width in
// the fallback face. A fit is held under this key and dropped when it changes.
export type Seen = { width: number; faces: number }
export function seenNext(seen: Seen, event: { width: number } | 'faces'): Seen {
  if (event === 'faces') return { ...seen, faces: seen.faces + 1 }
  // The same width is the same state, so a row taken off does not start it again.
  return event.width === seen.width ? seen : { ...seen, width: event.width }
}
export function fitKey(steps: Step[], seen: Seen): string {
  return steps.map((s) => s.key).join('|') + '@' + seen.width + '@' + seen.faces
}

// Tells of a change of the line's width and of fonts arriving, until the
// function it returns is called. The width watched is the whole line's, which
// the page decides; the list's own follows what is in it.
type Watched = { clientWidth: number }
type Env<E> = {
  Observer?: new (seen: () => void) => { observe: (el: E) => void; disconnect: () => void }
  fonts?: { ready: Promise<unknown>; addEventListener: (type: 'loadingdone', fn: () => void) => void; removeEventListener: (type: 'loadingdone', fn: () => void) => void }
}
export function watchLine<E extends Watched>(line: E | null | undefined, env: Env<E>, tell: (event: { width: number } | 'faces') => void): () => void {
  let live = true
  const faces = () => {
    if (live) tell('faces')
  }
  const sized = line && env.Observer ? new env.Observer(() => live && tell({ width: line.clientWidth })) : null
  if (line) sized?.observe(line)
  void env.fonts?.ready.then(faces)
  env.fonts?.addEventListener('loadingdone', faces)
  return () => {
    live = false
    sized?.disconnect()
    env.fonts?.removeEventListener('loadingdone', faces)
  }
}

const LOOSE: Fit = { tight: false, limit: null }

// The line, made tighter each time it is drawn until it fits. All the steps
// are drawn first and measured before the browser paints, so nothing is seen
// to jump. This component only joins the pieces above to the page: it holds
// what they decided, watches the line, and measures what was drawn.
export function FittedSteps(props: BarProps & { acts: boolean; onMore: LineProps['onMore'] }) {
  const list = useRef<HTMLUListElement>(null)
  const [seen, tell] = useReducer(seenNext, { width: 0, faces: 0 })
  const key = fitKey(props.steps, seen)
  const [held, setHeld] = useState<Fit & { key: string }>({ key: '', ...LOOSE })
  const fit: Fit = held.key === key ? held : LOOSE
  const drawn = props.acts && props.steps.length > 0

  useEffect(() => watchLine(list.current?.parentElement, { Observer: globalThis.ResizeObserver, fonts: document.fonts }, tell), [drawn])

  useLayoutEffect(() => {
    const ul = list.current
    if (!ul || fitsLine(stepBoxes(ul))) return
    const next = tighter(fit, props.steps.length)
    if (next) setHeld({ key, ...next })
  })

  return <NextStepLine {...props} limit={fit.limit} tight={fit.tight} listRef={list} />
}

// The same steps with a sentence each: what is true, and what the button does.
// In the order the line reads them, with the primary button on the same step.
// Each row is named by its step, so the note on the line can put the reader on
// the first step it had left off.
export function NextStepBar({ steps, onGo, onChanged }: BarProps) {
  const [first, ...rest] = inBarOrder(steps)
  const lead = leadStep(steps)
  return (
    <section className="vd-next" aria-labelledby="vd-next-h">
      <div className="vd-next-main" data-step={first?.key} tabIndex={-1}>
        <div className="vd-next-text">
          <h2 id="vd-next-h">Next step</h2>
          {first ? (
            <p>
              <span className={'vd-turn vd-turn-' + first.turn}>{turnWords(first)}</span> {first.text}
            </p>
          ) : (
            <p className="vd-quiet">Nothing is waiting on this vendor.</p>
          )}
        </div>
        {first && <Act step={first} primary={first === lead && !first.quiet} onGo={onGo} onChased={onChanged} />}
      </div>
      {rest.length > 0 && (
        <ul className="vd-next-rest" aria-label="Also on this vendor">
          {rest.map((s) => (
            <li key={s.key} data-step={s.key} tabIndex={-1}>
              <span className={'vd-turn vd-turn-' + s.turn}>{turnWords(s)}</span>
              <span className="vd-next-line">{s.text}</span>
              <Act step={s} primary={s === lead && !s.quiet} onGo={onGo} onChased={onChanged} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
